import { VrmAnimAttribute } from './attributes/VrmAnimAttribute';
import { ImageElement } from './elements/ImageElement';
import { ImageElement_v2 } from './elements/ImageElement_v2';
import { ImageSrcAttribute } from './attributes/ImageSrcAttribute';
import { SkyElement } from './elements/SkyElement';
import { glTFModelElement } from './elements/glTFModelElement';
import { ModelVrmElement } from './elements/ModelVrmElement';
import { VideoElement } from './elements/VideoElement';
import { SoundSrcAttribute } from './attributes/SoundSrcAttribute';
import { BlockTypeConsts } from './setting/BlockTypeConsts';
import { VR3InputAttribute } from './attributes/VR3InputAttribute';
import { ARGpsAttribute } from './attributes/ARGpsAttribute';
import { GlobalImport } from '../utils/GlobalImport';
const Blockly = GlobalImport.getBlockly();

import { blocklyDispatcher } from './blocklyDispatcher';
import { blocklyStore } from './blocklyStore';
import { ModelViewSceneSimpleBlock } from './elements/ModelViewSceneSimpleBlock';
import { block_translation } from '../lang/BlockTranslation';
import { BlockElement } from './elements/BlockElement';
import { ShapeSettings } from './setting/ShapeSettings';
const t = block_translation;

export class BlockFactory {
  /**
   * ワークスペースにブロックXMLを追加する共有関数
   */
  addWorkspace(selected_xml: HTMLElement): void {
    if (selected_xml) {
      const xml = selected_xml;
      Blockly.Xml.textToDom(xml);
      Blockly.Xml.domToBlock(blocklyStore.workspace, xml, true);
    }
  }

  /**
   * ブロック画面をマウントした後にワークスペースにブロックXMLを追加する共有関数
   */
  addWorkspaceAfterMounted(selected_xml: HTMLElement): void {
    blocklyDispatcher.addMountedWorkspaceListener((workspace) => {
      if (selected_xml) {
        const xml = selected_xml;
        Blockly.Xml.textToDom(xml);
        Blockly.Xml.domToBlock(workspace, xml, true);
      }
    });
  }

  // 全天球+属性ブロックを作成してワークスペースに追加
  getSkyBlock(filePath: string): HTMLElement {
    const srcAtt = new ImageSrcAttribute();
    const srcBlock = srcAtt.addDefaultKeyValue(srcAtt.SRC_FIELD_NAME, filePath);
    const sky = new SkyElement(BlockTypeConsts.SKY, t.to('全天球', 'sky'));
    sky.setAttr(srcBlock);
    return sky.toXml();
  }

  getBox(color: string): BlockElement {
    const box = new BlockElement(BlockTypeConsts.CUBE, t.to('直方体', 'box'));
    box.setNameForCode('box');

    return box;
    // const videoBlock = new VideoElement().addDefaultKeyValue('src', path);
    // // let scaleBlock = new ScaleAttribute().addDefaultKeyValue('scale-x' , '2' );
    // const scaleBlock = new VR3InputAttribute('scale_v2', t.to('大きさ', 'scale'))
    //   .setNameForCode('scale')
    //   .setDefaultXYZ(1, 1, 1);
    // scaleBlock.addDefaultKeyValue('scale-x', 2);
    // scaleBlock.addDefaultKeyValue('scale-y', 1);
    // videoBlock.setAttr(scaleBlock);

    // return videoBlock.toXml();
  }

  // 属性ブロックを作成してワークスペースに追加
  getSrcAttribute(filePath: string): Promise<ImageSrcAttribute> {
    return new Promise((resolve, reject) => {
      try {
        const image = new ImageSrcAttribute();
        const srcBlock = image.addDefaultKeyValue(image.SRC_FIELD_NAME, filePath);
        resolve(srcBlock);
      } catch (error) {
        reject(error);
      }
    });
  }

  // 属性ブロックを作成してワークスペースに追加
  getImageSrcAttribute(filePath: string): Promise<ImageSrcAttribute> {
    return new Promise((resolve, reject) => {
      try {
        const image = new ImageSrcAttribute();
        const srcBlock = image.addDefaultKeyValue(image.SRC_FIELD_NAME, filePath);

        const img = document.createElement('img'); // 新たな img 要素を作成
        const onloadFunc = () => {
          const width = img.naturalWidth / 100;
          const height = img.naturalHeight / 100;

          const scaleAtt = new VR3InputAttribute('scale_v2', t.to('大きさ', 'scale'))
            .setNameForCode('scale')
            .setDefaultXYZ(1, 1, 0);

          scaleAtt.addDefaultKeyValue('scale-x', width);
          scaleAtt.addDefaultKeyValue('scale-y', height);
          scaleAtt.addDefaultKeyValue('scale-z', 0);
          srcBlock.setAttr(scaleAtt);

          resolve(srcBlock);
        };
        img.addEventListener('load', onloadFunc);
        img.src = filePath;
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  // 属性ブロックを作成してワークスペースに追加
  getImageElement(filePath: string): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      try {
        const imgBlock = new ImageElement();

        const img = document.createElement('img'); // 新たな img 要素を作成
        const onloadFunc = () => {
          const width = img.naturalWidth / 100;
          const height = img.naturalHeight / 100;

          imgBlock.addDefaultKeyValue('scale-x', width);
          imgBlock.addDefaultKeyValue('scale-y', height);
          imgBlock.addDefaultKeyValue('scale-z', 0);
          imgBlock.addDefaultKeyValue('path', filePath);

          // ワークスペースへ追加
          resolve(imgBlock.toXml());
        };

        img.addEventListener('load', onloadFunc);
        img.src = filePath;
      } catch (error) {
        reject(error);
      }
    });
  }

  // 属性ブロックを作成してワークスペースに追加
  getImageElementV2(filePath: string): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      try {
        const imgBlock = new ImageElement_v2();
        const scaleAtt = new VR3InputAttribute('scale_v2', t.to('大きさ', 'scale'))
          .setNameForCode('scale')
          .setDefaultXYZ(1, 1, 0);

        const img = document.createElement('img'); // 新たな img 要素を作成
        const onloadFunc = () => {
          const width = img.naturalWidth / 100;
          const height = img.naturalHeight / 100;

          scaleAtt.addDefaultKeyValue('scale-x', width);
          scaleAtt.addDefaultKeyValue('scale-y', height);
          scaleAtt.addDefaultKeyValue('scale-z', 0);
          imgBlock.setAttr(scaleAtt);
          imgBlock.addDefaultKeyValue('path', filePath);

          // ワークスペースへ追加
          resolve(imgBlock.toXml());
        };

        img.addEventListener('load', onloadFunc);
        img.src = filePath;
      } catch (error) {
        reject(error);
      }
    });
  }

  getGlTFModel(path: string): HTMLElement {
    const name = path.split('/').reverse()[0];

    const gltfModel = new glTFModelElement()
      .addDefaultKeyValue('src', path)
      .addDefaultKeyValue('nameField', name);

    // ワークスペースへ追加
    return gltfModel.toXml();
  }

  getVRMModel(path: string): HTMLElement {
    const name = path.split('/').reverse()[0];

    const vrmModel = new ModelVrmElement()
      .addDefaultKeyValue('src', path)
      .addDefaultKeyValue('nameField', name);

    // ワークスペースへ追加
    return vrmModel.toXml();
  }

  getVRMAnimation(path: string): HTMLElement {
    const vrmAnimAttribute = new VrmAnimAttribute().addDefaultKeyValue('src', path);

    // ワークスペースへ追加
    return vrmAnimAttribute.toXml();
  }

  getModelViewerSimpleModel(path: string): HTMLElement {
    const name = path.split('/').reverse()[0];
    const gltfModel = new ModelViewSceneSimpleBlock()
      .addDefaultKeyValue('src', path)
      .addDefaultKeyValue('nameField', name);
    // ワークスペースへ追加
    return gltfModel.toXml();
  }

  getVideo(path: string): HTMLElement {
    // console.log('addVideoToWorkspace', path);
    const videoBlock = new VideoElement().addDefaultKeyValue('src', path);
    // let scaleBlock = new ScaleAttribute().addDefaultKeyValue('scale-x' , '2' );
    const scaleBlock = new VR3InputAttribute('scale_v2', t.to('大きさ', 'scale'))
      .setNameForCode('scale')
      .setDefaultXYZ(1, 1, 1);
    scaleBlock.addDefaultKeyValue('scale-x', 2);
    scaleBlock.addDefaultKeyValue('scale-y', 1);
    videoBlock.setAttr(scaleBlock);

    return videoBlock.toXml();
  }

  getSound(path: string): HTMLElement {
    const soundBlock = new SoundSrcAttribute()
      .addDefaultKeyValue('src', path)
      .addDefaultKeyValue('volume', '5');

    // ワークスペースへ追加
    return soundBlock.toXml();
  }

  // GPSブロックを作成してワークスペースに追加
  getGPSAttribute(latitude: number, longitude: number): HTMLElement {
    const srcBlock = new ARGpsAttribute()
      .addDefaultKeyValue('latitude', latitude)
      .addDefaultKeyValue('longitude', longitude);
    return srcBlock.toXml();
  }
}
